import { Component } from '@theme/component';

/** How fast the drawn turn catches up with the scroll position (0–1 per frame). Lower = glidier. */
const EASE = 0.16;

/**
 * <tmc-spin>: Apple-product-page-style scrub section. The figure starts turning as the section scrolls
 * into view and finishes as it settles, held (CSS sticky) in the centre of the screen. Feature notes then
 * scroll past it in normal page flow (under it on phones, alternating sides on desktop); each one lights
 * up while it is in its reading band.
 *
 * Frames are transparent stills cut from a turntable video, drawn onto a canvas; the poster <img>
 * (frame 1) is real markup, so no-JS and slow connections still see the figure. They load once the
 * section is near the screen after page load, coarse-to-fine (every 8th, 4th, 2nd, then the rest), so the
 * turn works early and only gets smoother. lite tier: every 2nd frame. reduced / off: no held screen,
 * notes stacked and visible, the figure turns if dragged.
 *
 * @typedef {{ scrub: HTMLElement, turn: HTMLElement, stage: HTMLElement, canvas: HTMLCanvasElement, poster: HTMLImageElement, notes: HTMLElement[] }} Refs
 * @extends {Component<Refs>}
 */
class TmcSpin extends Component {
  requiredRefs = ['scrub', 'stage', 'canvas', 'poster'];

  /** @type {(HTMLImageElement | null)[]} */
  #frames = [];
  /** @type {string[]} */
  #urls = [];
  #current = -1;
  /** Turn progress the scroll asks for (0–1), and the eased value being drawn. */
  #goal = 0;
  #shown = 0;
  #mode = /** @type {'scroll' | 'drag'} */ ('drag');
  #raf = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver[]} */
  #observers = [];
  /** @type {ResizeObserver | null} */
  #resizer = null;
  #visible = false;
  #loading = false;
  /** @type {{ x: number, progress: number } | null} */
  #drag = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    Promise.all([supportsAvif(), motionRuntime()]).then(([avif, motion]) => {
      this.#urls = parseList((avif && this.dataset.framesAvif) || this.dataset.frames);
      if (this.#urls.length < 2) return;
      this.#frames = this.#urls.map(() => null);
      this.#start(motion);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    for (const observer of this.#observers) observer.disconnect();
    this.#resizer?.disconnect();
    cancelAnimationFrame(this.#raf);
  }

  /** @param {any} motion */
  #start(motion) {
    const tier = motion.tier(this.dataset.motion);
    const signal = this.#abort?.signal;
    this.#mode = tier === 'full' || tier === 'lite' ? 'scroll' : 'drag';
    this.dataset.mode = this.#mode;
    const step = tier === 'lite' ? 2 : 1;

    this.#resizer = new ResizeObserver(() => this.#resize());
    this.#resizer.observe(this.refs.stage);

    const near = new IntersectionObserver(
      ([entry]) => {
        this.#visible = Boolean(entry?.isIntersecting);
        if (!this.#visible) return;
        afterLoad().then(() => this.#load(step));
        if (this.#mode === 'scroll') this.#follow();
      },
      { rootMargin: '150% 0px' }
    );
    near.observe(this);
    this.#observers.push(near);

    if (this.#mode === 'scroll') {
      // Capture on document: on desktop Horizon scrolls .page-wrapper, not the window, and scroll does not bubble.
      const onScroll = () => this.#visible && this.#follow();
      document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal });
      window.addEventListener('resize', onScroll, { passive: true, signal });
      this.#follow();
      this.#shown = this.#goal;
      this.#watchNotes();
    } else {
      const { stage } = this.refs;
      stage.addEventListener('pointerdown', (event) => this.#dragStart(event), { signal });
      stage.addEventListener('pointermove', (event) => this.#dragMove(event), { signal });
      stage.addEventListener('pointerup', () => (this.#drag = null), { signal });
      stage.addEventListener('pointercancel', () => (this.#drag = null), { signal });
    }
  }

  /** A note is lit while it sits in the reading band: below the figure on phones, mid-screen on desktop. */
  #watchNotes() {
    const notes = this.refs.notes ?? [];
    if (!notes.length) return;
    const wide = matchMedia('(min-width: 990px)').matches;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) entry.target.parentElement?.toggleAttribute('data-lit', entry.isIntersecting);
      },
      // Phones: the band under the figure, and only when the words are wholly inside it.
      wide ? { rootMargin: '-28% 0px -28% 0px' } : { rootMargin: '-66% 0px 0px 0px', threshold: 0.99 }
    );
    // Watch the words, not the tall note box, so a note goes dark as soon as its text leaves the band.
    for (const note of notes) observer.observe(note.firstElementChild ?? note);
    this.#observers.push(observer);
  }

  /** Loads frames coarse-to-fine; each one is decoded before it can be drawn. @param {number} step */
  async #load(step) {
    if (this.#loading) return;
    this.#loading = true;
    const total = this.#urls.length;
    const order = [];
    const seen = new Set();
    for (const stride of [8, 4, 2, 1]) {
      if (stride < step) break;
      for (let i = 0; i < total; i += stride) {
        if (!seen.has(i)) {
          seen.add(i);
          order.push(i);
        }
      }
    }
    if (!seen.has(total - 1)) order.push(total - 1);

    // A few at a time: fast on good networks, never floods a slow one.
    const queue = [...order];
    const worker = async () => {
      while (queue.length && !this.#abort?.signal.aborted) {
        const index = /** @type {number} */ (queue.shift());
        const image = new Image();
        image.decoding = 'async';
        image.src = /** @type {string} */ (this.#urls[index]);
        try {
          await image.decode();
          this.#frames[index] = image;
          this.#current = -1;
          this.#schedule();
        } catch {
          /* skip a frame that fails; neighbours cover it */
        }
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    this.dataset.loaded = '';
  }

  /** Turn runs from "scrub area enters at the bottom" to "the turn spacer has scrolled past the top". */
  #follow() {
    const top = this.refs.scrub.getBoundingClientRect().top;
    const height = window.innerHeight;
    const distance = height + (this.refs.turn?.offsetHeight ?? height * 0.6);
    this.#goal = Math.min(1, Math.max(0, (height - top) / distance));
    this.#schedule();
  }

  /** @param {PointerEvent} event */
  #dragStart(event) {
    this.#drag = { x: event.clientX, progress: this.#goal };
    this.refs.stage.setPointerCapture?.(event.pointerId);
  }

  /** @param {PointerEvent} event */
  #dragMove(event) {
    if (!this.#drag) return;
    const width = this.refs.stage.clientWidth || 1;
    const progress = this.#drag.progress + (event.clientX - this.#drag.x) / width;
    this.#goal = ((progress % 1) + 1) % 1;
    this.#shown = this.#goal;
    this.#schedule();
  }

  #schedule() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#tick();
    });
  }

  #tick() {
    const gap = this.#goal - this.#shown;
    this.#shown = Math.abs(gap) < 0.0005 ? this.#goal : this.#shown + gap * EASE;
    this.#draw(Math.round(this.#shown * (this.#urls.length - 1)));
    if (this.#shown !== this.#goal) this.#schedule();
  }

  /** Nearest decoded frame, so the turn works before every frame has arrived. @param {number} target */
  #nearest(target) {
    for (let distance = 0; distance < this.#frames.length; distance++) {
      if (this.#frames[target - distance]) return target - distance;
      if (this.#frames[target + distance]) return target + distance;
    }
    return -1;
  }

  #resize() {
    const { canvas, stage } = this.refs;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(stage.clientWidth * ratio);
    canvas.height = Math.round(stage.clientHeight * ratio);
    const context = canvas.getContext('2d');
    if (context) context.imageSmoothingQuality = 'high';
    this.#current = -1;
    this.#schedule();
  }

  /** @param {number} target */
  #draw(target) {
    const index = this.#nearest(target);
    if (index < 0 || index === this.#current) return;
    const image = /** @type {HTMLImageElement} */ (this.#frames[index]);
    const { canvas } = this.refs;
    const context = canvas.getContext('2d');
    if (!context || !canvas.width) return;
    const scale = Math.min(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
    const width = image.naturalWidth * scale;
    const height = image.naturalHeight * scale;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
    this.#current = index;
    this.dataset.drawn = '';
    this.dataset.frame = String(index + 1);
  }
}


/** @param {string | undefined} json */
function parseList(json) {
  try {
    const list = JSON.parse(json ?? '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

/** @type {Promise<boolean> | null} */
let avifSupport = null;

/** Decodes a 1×1 AVIF once to see whether this browser can show AVIF frames. */
function supportsAvif() {
  avifSupport ??= new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image.width > 0);
    image.onerror = () => resolve(false);
    image.src =
      'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
  });
  return avifSupport;
}

/** @returns {Promise<void>} */
function afterLoad() {
  if (document.readyState === 'complete') return Promise.resolve();
  return new Promise((resolve) => window.addEventListener('load', () => resolve(), { once: true }));
}

/** @returns {Promise<any>} window.TMC.motion, once the runtime has run */
function motionRuntime() {
  const ready = /** @type {any} */ (window).TMC?.motion;
  if (ready) return Promise.resolve(ready);
  return new Promise((resolve) => {
    window.addEventListener('tmc:motion-ready', () => resolve(/** @type {any} */ (window).TMC.motion), { once: true });
  });
}

// Registered last: defining it upgrades elements already on the page at once, so every helper above must exist first.
if (!customElements.get('tmc-spin')) customElements.define('tmc-spin', TmcSpin);
